import { CallState } from '../../features/call/call.types';
import { type ServiceTokens } from '../../container';
import {
  GenericCallService,
  type GenericCallServiceDependencies,
} from './genericCall.service';
import { type PatientRealtimeService } from './realtime/patientRealtime.service';
import { CallTransition } from '../../features/call/call.types';
import { ManualVideoQuality } from './interface';
import { RealtimeMessageType } from '../../features/realtime/realtime.types';
import { getDefaultVideoQuality } from '../../shared/deviceCapabilities/getDefaultVideoQuality';

type PatientCallServiceDependencies = {
  [ServiceTokens.PatientRealtime]: PatientRealtimeService;
} & GenericCallServiceDependencies;

type PatientCallStartParams = {
  isAudioCall: boolean;
  sessionId: string;
  identityId: string;
  joinToken: string;
  providerUserId?: string;
  providerName?: string;
};

type PatientCallResumeParams = {
  isAudioCall: boolean;
  sessionId: string;
  identityId: string;
  joinToken: string;
};

export class PatientCallService extends GenericCallService {
  protected override readonly realtime: PatientRealtimeService;

  constructor(dependencies: PatientCallServiceDependencies) {
    super(dependencies);

    this.realtime = dependencies.patientRealtime;
    this.listenToManualVideoQuality();
  }

  async start(
    {
      isAudioCall,
      identityId,
      sessionId,
      joinToken,
      providerUserId,
      providerName,
    }: PatientCallStartParams = {} as unknown as PatientCallStartParams,
  ): Promise<void> {
    if (
      this.stateMachine.getSnapshot().matches(CallState.Initializing) ||
      this.stateMachine.getSnapshot().matches(CallState.Resuming)
    ) {
      return;
    }
    try {
      this.stateMachine.send(CallTransition.START);
      if (isAudioCall) {
        void this.media
          .toggleVideo(false)
          .catch((error) => this.logger.warn(error));
      }
      await this.session.join({ sessionId, identityId, joinToken });
      await this.session.connect();
      void this.stateMachine.send(CallTransition.INIT_DONE);
      this.realtime.setStatusToBusy(providerUserId, providerName, sessionId);
    } catch (error) {
      this.logger.error('start call failed', error);
      void this.stateMachine.send(CallTransition.INIT_FAILED);
    }
  }

  async stop(): Promise<void> {
    this.logger.debug('stopped');
  }

  async hold(
    sessionOwnershipToken: string,
    dbSessionId?: string,
  ): Promise<void> {
    void this.stateMachine.send(CallTransition.HOLD);
    // TODO: add db session id and timing information
    if (typeof this.session.sessionId === 'string') {
      this.realtime.setStatusToOnHold({
        sessionId: this.session.sessionId,
        dbSessionId,
        sessionOwnershipToken,
      });
    }

    void this.session.hold();
  }

  async resume(
    {
      identityId,
      sessionId,
      joinToken,
    }: PatientCallResumeParams = {} as unknown as PatientCallResumeParams,
  ): Promise<void> {
    try {
      void this.stateMachine.send(CallTransition.RESUME);
      this.session.clearParticipants();
      await this.session.join({ sessionId, identityId, joinToken });
      await this.session.connect();
      void this.stateMachine.send(CallTransition.INIT_DONE);
      this.realtime.setStatusToBusyOnResume();
    } catch (error) {
      this.logger.error('resume call failed', error);
      void this.stateMachine.send(CallTransition.INIT_FAILED);
    }
  }

  private async setManualVideoQuality(
    manualVideoQuality: ManualVideoQuality,
  ): Promise<void> {
    if (this._manualVideoQuality === manualVideoQuality) {
      return;
    }

    this._manualVideoQuality = await this.setVideoQuality(
      manualVideoQuality || getDefaultVideoQuality(),
      'provider set quality',
    );
  }

  private listenToManualVideoQuality() {
    this.realtime.on(RealtimeMessageType.ManualVideoQuality, (data) => {
      this.setManualVideoQuality(data.payload as ManualVideoQuality);
    });
  }
}
